import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { availableCors } from '../../../../../../core/projects.selectors';
import { ProjectsService } from '../../../../../../core/projects.service';
import {
  CreateChangeOrderFromRequestsRequest,
  FetchAvailableCorsRequest,
  LinkRequestsToChangeOrderRequest,
} from '../../../../../../core/projects.actions';
import { CommonService } from '../../../../../../../../shared/services/common.service';

// change_order.comments is varchar(5000) in Postgres, so this is a real backend
// limit rather than a UI preference. It is enforced as an explicit validator
// because the shared text-area only sets attr.maxlength, which the DOM ignores
// for programmatic setValue — and dropping the cap on an already-long value.
const CHANGE_ORDER_COMMENTS_MAX = 5000;

@Component({
  selector: 'app-create-change-order-dialog',
  templateUrl: './create-change-order-dialog.component.html',
  styleUrls: ['./create-change-order-dialog.component.scss'],
  standalone: false,
})
export class CreateChangeOrderDialogComponent implements OnInit, OnDestroy {
  @Input() data: any;
  availableCors: any[] = [];
  selectedCorIds: Set<string> = new Set();
  form = new FormGroup({
    orderNumber: new FormControl('', [Validators.required]),
    comments: new FormControl('', [Validators.maxLength(CHANGE_ORDER_COMMENTS_MAX)]),
    location: new FormControl(''),
  });
  linkMode = false;
  // change_order.comments is varchar(5000); the shared text-area otherwise
  // defaults to 2000, which the auto-composed description of several requests
  // silently exceeded (programmatic setValue bypasses the DOM maxlength).
  descriptionMaxLength = CHANGE_ORDER_COMMENTS_MAX;
  // Tracks the last auto-generated values for comments/location. We only
  // overwrite the form controls when their current value still matches the
  // last auto-generated value (or is empty) — that way a user edit is never
  // clobbered by a subsequent COR (de)selection.
  private lastAutoComments = '';
  private lastAutoLocation = '';
  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store,
    private commonService: CommonService,
    private projectsService: ProjectsService,
  ) {}

  ngOnInit() {
    this.linkMode = !!this.data?.linkMode;

    this.store.dispatch(FetchAvailableCorsRequest({
      payload: { projectId: this.data.projectId }
    }));

    this.store.select(availableCors)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        // Ascending by request number (numeric-aware, matching the CIWR list sort)
        this.availableCors = (data?.list || []).slice().sort((a, b) =>
          String(a?.request_no || '').localeCompare(String(b?.request_no || ''), undefined, { numeric: true }),
        );
      });
  }

  get commentsError(): string {
    const errors = this.form.get('comments')?.errors;
    return errors?.['maxlength']
      ? `description exceeds ${errors['maxlength'].requiredLength} characters — please shorten it`
      : '';
  }

  toggleCor(corId: string) {
    if (this.selectedCorIds.has(corId)) {
      this.selectedCorIds.delete(corId);
    } else {
      this.selectedCorIds.add(corId);
    }
    this.syncAutoFields();
  }

  /**
   * Rebuild the auto-generated description + location values from the currently
   * selected CORs and push them into the form ONLY if the user hasn't manually
   * edited those fields. Description: numbered concat of each COR's description.
   * Location: fuzzy-deduplicated newline-joined list of every COR line's
   * `location` (see `normalizeLocation` below).
   */
  private syncAutoFields() {
    const selected = this.availableCors.filter(cor => this.selectedCorIds.has(cor.id));

    const autoComments = selected
      .map((cor, i) => cor.description ? `Change request ${cor.request_no || i + 1}. ${cor.description}` : null)
      .filter(Boolean)
      .join('\n\n');

    // Dedupe by normalized form but keep the first original string we saw.
    // That way "STA 120+00 to STA 145+00" and "Sta. 120+00 to 145+00" collapse
    // to a single line while preserving whatever capitalization the user typed.
    const seenKeys = new Set<string>();
    const orderedLocations: string[] = [];
    selected.forEach(cor => {
      (cor.lines || []).forEach((line: any) => {
        const raw = (line?.location || '').trim();
        if (!raw) return;
        const key = CreateChangeOrderDialogComponent.normalizeLocation(raw);
        if (!key || seenKeys.has(key)) return;
        seenKeys.add(key);
        orderedLocations.push(raw);
      });
    });
    const autoLocation = orderedLocations.join('\n');

    const commentsCtrl = this.form.get('comments');
    if (commentsCtrl && (!commentsCtrl.value || commentsCtrl.value === this.lastAutoComments)) {
      commentsCtrl.setValue(autoComments);
    }
    this.lastAutoComments = autoComments;

    const locationCtrl = this.form.get('location');
    if (locationCtrl && (!locationCtrl.value || locationCtrl.value === this.lastAutoLocation)) {
      locationCtrl.setValue(autoLocation);
    }
    this.lastAutoLocation = autoLocation;
  }

  /**
   * Collapse common real-world variations of station/location strings down to
   * a comparable key. Examples that all normalize to `120+00-145+00`:
   *   "STA 120+00 to STA 145+00"
   *   "Sta. 120+00 to 145+00"
   *   "120+00 – 145+00"
   *   "STA 120+00-145+00"
   */
  private static normalizeLocation(s: string): string {
    if (!s) return '';
    return s
      .toLowerCase()
      .replace(/[\u2013\u2014]/g, '-') // en/em dash → hyphen
      .replace(/\bstation\b|\bsta\.?\b/g, '')
      .replace(/\bto\b/g, '-')
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9+\-]/g, '')
      .trim();
  }

  confirm() {
    const corIds = Array.from(this.selectedCorIds);
    if (corIds.length === 0) return;
    if (!this.linkMode && this.form.invalid) return;

    if (this.linkMode) {
      this.store.dispatch(LinkRequestsToChangeOrderRequest({
        payload: {
          changeOrderId: this.data.changeOrderId,
          body: { cor_ids: corIds },
        }
      }));
    } else {
      this.store.dispatch(CreateChangeOrderFromRequestsRequest({
        payload: {
          project_id: this.data.projectId,
          order_number: this.form.value.orderNumber,
          comments: this.form.value.comments,
          location: this.form.value.location,
          cor_ids: corIds,
        }
      }));
    }

    this.close();
  }

  close() {
    if (this.linkMode && this.data?.changeOrderId) {
      // Go back to the CO detail panel
      const { ChangeOrdersRequestSideNavWrapperComponent } = require('../change-orders-request-side-nav-wrapper/change-orders-request-side-nav-wrapper.component');
      this.projectsService.openRightPanel.next({
        component: ChangeOrdersRequestSideNavWrapperComponent,
        data: { id: this.data.changeOrderId, createRequest: false },
      });
    } else {
      this.projectsService.closeRightPanel.next();
    }
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
